/**
 * DatasetTable - Table component for displaying datasets
 * @module components/starnexus/datasets/DatasetTable
 *
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import ButtonDropdown from '@amzn/awsui-components-console/button-dropdown';
import Link from '@amzn/awsui-components-console/link';
import Pagination from '@amzn/awsui-components-console/pagination';
import CollectionPreferences from '@amzn/awsui-components-console/collection-preferences';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import type { Dataset } from '../../../types/starnexus';
import type { DatasetSortConfig } from '../../../services/starnexus';

const STATUS_MAP: Record<string, { type: 'success'|'pending'|'in-progress'|'error'|'stopped'|'warning'|'info'; label: string }> = {
  active:        { type: 'success',     label: 'Active' },
  draft:         { type: 'pending',     label: 'Draft' },
  processing:    { type: 'in-progress', label: 'Processing' },
  failed:        { type: 'error',       label: 'Failed' },
  archived:      { type: 'stopped',     label: 'Archived' },
  andes_deleted: { type: 'warning',     label: 'Andes Deleted' },
};

interface DatasetTableProps {
  datasets: Dataset[];
  loading: boolean;
  selectedItems: Dataset[];
  onSelectionChange: (items: Dataset[]) => void;
  sortingColumn: DatasetSortConfig | null;
  onSortingChange: (config: DatasetSortConfig | null) => void;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onAction: (action: string, dataset: Dataset) => void;
  onBulkAction: (action: string) => void;
}

export const DatasetTable: React.FC<DatasetTableProps> = ({
  datasets,
  loading,
  selectedItems,
  onSelectionChange,
  sortingColumn,
  onSortingChange,
  currentPage,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onAction,
  onBulkAction
}) => {
  const navigate = useNavigate();

  // Format date for display
  const formatDate = (date: string | Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Column definitions
  const columnDefinitions = [
    {
      id: 'datasetName',
      header: 'Dataset name',
      cell: (item: Dataset) => (
        <Link onFollow={() => navigate(`/starnexus/uploader/datasets/${item.datasetId}`)}>
          {item.datasetName}
        </Link>
      ),
      sortingField: 'datasetName',
      width: 200
    },
    {
      id: 'andesTable',
      header: 'Andes table',
      cell: (item: Dataset) => (
        <Box color="text-status-info">{`${item.providerName || '-'}.${item.tableName || '-'}`}</Box>
      ),
      sortingField: 'providerName',
      width: 220
    },
    {
      id: 'teamName',
      header: 'Team',
      cell: (item: Dataset) => item.teamName,
      sortingField: 'teamName',
      width: 150
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: Dataset) => {
        const status = STATUS_MAP[item.status] || { type: 'info' as const, label: item.status || 'Unknown' };
        return <StatusIndicator type={status.type}>{status.label}</StatusIndicator>;
      },
      sortingField: 'status',
      width: 130
    },
    {
      id: 'batches',
      header: 'Batches',
      cell: (item: Dataset) => (item.batchCount ?? 0).toLocaleString(),
      sortingField: 'batchCount',
      width: 100
    },
    {
      id: 'totalRows',
      header: 'Total rows',
      cell: (item: Dataset) => (item.totalRowCount ?? 0).toLocaleString(),
      sortingField: 'totalRowCount',
      width: 130
    },
    {
      id: 'lastUpdated',
      header: 'Last updated',
      cell: (item: Dataset) => formatDate(item.lastBatchDate || item.updatedAt),
      sortingField: 'updatedAt',
      width: 160
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item: Dataset) => (
        <ButtonDropdown
          items={[
            { id: 'view', text: 'View details' },
            { id: 'resume', text: 'Resume creation', disabled: !(item.status === 'failed' || item.status === 'draft') },
            { id: 'upload', text: 'Upload new snapshot', disabled: item.status !== 'active' },
            { id: 'edit', text: 'Edit configuration' },
            {
              text: 'Lifecycle',
              items: [
                { id: 'archive', text: 'Archive', disabled: item.status === 'archived' || item.status === 'andes_deleted' },
                { id: 'reactivate', text: 'Reactivate', disabled: item.status !== 'archived' },
              ],
            },
            {
              text: 'Danger zone',
              items: [
                {
                  id: 'delete',
                  text: 'Delete permanently',
                  disabled: item.status === 'processing',
                },
              ],
            },
          ]}
          onItemClick={({ detail }) => onAction(detail.id, item)}
          variant="inline-icon"
          expandableGroups
          expandToViewport
          ariaLabel={`Actions for ${item.datasetName}`}
        />
      ),
      width: 80
    }
  ];

  // Handle sorting change
  const handleSortingChange = (event: any) => {
    const { sortingColumn: col, isDescending } = event.detail;
    if (col) {
      onSortingChange({
        field: col.sortingField as keyof Dataset,
        direction: isDescending ? 'desc' : 'asc'
      });
    } else {
      onSortingChange(null);
    }
  };

  // Empty state
  const emptyState = (
    <Box textAlign="center" color="inherit" padding="l">
      <SpaceBetween size="m">
        <b>No datasets</b>
        <Box variant="p" color="inherit">
          No datasets match your search criteria, or you haven't created any
          datasets yet. Use the "Create dataset" button in the page header to start.
        </Box>
      </SpaceBetween>
    </Box>
  );

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={datasets}
      loading={loading}
      loadingText="Loading datasets"
      selectionType="multi"
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) =>
        onSelectionChange(detail.selectedItems)
      }
      sortingColumn={
        sortingColumn
          ? columnDefinitions.find(
              (c) => c.sortingField === sortingColumn.field
            )
          : undefined
      }
      sortingDescending={sortingColumn?.direction === 'desc'}
      onSortingChange={handleSortingChange}
      empty={emptyState}
      trackBy="datasetId"
      header={
        <Header
          counter={`(${totalCount})`}
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <ButtonDropdown
                items={[
                  { id: 'reactivate', text: 'Reactivate' },
                  { id: 'archive', text: 'Archive' },
                  {
                    text: 'Danger zone',
                    items: [
                      { id: 'delete', text: 'Delete permanently' },
                      { id: 'export', text: 'Export metadata' },
                    ],
                  },
                ]}
                onItemClick={({ detail }) => onBulkAction(detail.id)}
                disabled={selectedItems.length === 0}
                expandableGroups
              >
                Actions
              </ButtonDropdown>
            </SpaceBetween>
          }
        >
          Datasets
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          pagesCount={totalPages}
          onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
        />
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={{ pageSize }}
          pageSizePreference={{
            options: [
              { value: 10, label: '10 datasets' },
              { value: 25, label: '25 datasets' },
              { value: 50, label: '50 datasets' },
            ]
          }}
          onConfirm={({ detail }) => {
            if (onPageSizeChange) {
              onPageSizeChange(detail.pageSize || 10);
            }
          }}
        />
      }
      stickyHeader
      variant="full-page"
    />
  );
};

export default DatasetTable;
